// applyBranding — apply an org's resolved branding to the document globally.
// Mirrors usePolarity's `applyToDOM`: a standalone, side-effecting function the
// BrandingProvider invokes (in an effect) whenever branding or resolved polarity
// changes. Sets the :root accent custom properties (polarity-aware), the favicon,
// and the document title. Color values are contract-guaranteed #rrggbb (WS-A HEX
// regex) and are written via style.setProperty — an invalid value is a no-op, and
// a custom-property *value* cannot break out into arbitrary CSS, so there is no
// injection vector even if the contract were violated (defence in depth).
import type { OrgBranding, DeploymentBranding } from '@zion/sdk-cp-dp-contracts';
import type { ResolvedPolarity } from '@zion/sdk-contracts';

/** Neutral fallback product name — used for the tab title and the fully-unset
 *  wordmark when an org sets no productName. [DESIGN-CONFIRM DC-3] */
export const NEUTRAL_PRODUCT_NAME = 'Console';

/** Neutral favicon (muted rounded square). MUST stay byte-identical to the
 *  `<link rel="icon">` default in index.html (asserted by index-html test). [DC-5] */
export const NEUTRAL_FAVICON_HREF =
  "data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 32 32'>" +
  "<rect x='4' y='4' width='24' height='24' rx='7' fill='%23787e91'/></svg>";

/** The accent + secondary-accent for the active polarity, or null when unset.
 *  Precedence per field: per-org (`branding`) → deployment tier → null. */
export function accentFor(
  branding: OrgBranding | null,
  resolved: ResolvedPolarity,
  deployment: DeploymentBranding | null = null,
): { accent: string | null; accentSecondary: string | null } {
  const dark = resolved === 'dark';
  const pick = (o: OrgBranding | DeploymentBranding | null, k: 'primaryColor' | 'accentSecondary') => {
    if (!o) return null;
    return (dark ? o[`${k}Dark` as const] : o[k]) ?? null;
  };
  return {
    accent: pick(branding, 'primaryColor') ?? pick(deployment, 'primaryColor'),
    accentSecondary: pick(branding, 'accentSecondary') ?? pick(deployment, 'accentSecondary'),
  };
}

/**
 * On-accent foreground (D23): black or white — whichever meets WCAG AA against the `#rrggbb`
 * accent — so solid-accent controls stay legible for ARBITRARY tenant brand colors (the schema has
 * no contrast floor; a fixed `.on-accent: black` is AA only for the Zion stops). Relative-luminance
 * per WCAG 2.x; pick the higher-contrast of black vs white. `null` accent (token default) → `null`
 * (let `.on-accent` use its CSS default). A malformed hex → black (defensive; contract guarantees
 * `#rrggbb`). KEEP THE LUMINANCE ALGORITHM (the black-vs-white decision) IN SYNC with WS-D's
 * `onAccentFor` so the CP preview matches — only the output FORMAT differs (this returns CSS
 * `rgb(...)` for the custom-prop; WS-D returns `#000000`/`#ffffff` for an inline style).
 */
export function onAccentFor(accent: string | null): string | null {
  if (accent === null) return null;
  const match = /^#([0-9a-fA-F]{6})$/.exec(accent);
  if (!match) return 'rgb(0 0 0)';
  const n = parseInt(match[1], 16);
  const toLinear = (c: number): number => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  const L =
    0.2126 * toLinear((n >> 16) & 0xff) +
    0.7152 * toLinear((n >> 8) & 0xff) +
    0.0722 * toLinear(n & 0xff);
  const contrastBlack = (L + 0.05) / 0.05;
  const contrastWhite = 1.05 / (L + 0.05);
  return contrastBlack >= contrastWhite ? 'rgb(0 0 0)' : 'rgb(255 255 255)';
}

/** The logo URL for the active polarity (dark prefers darkUrl, falls back to
 *  lightUrl, and vice-versa), or null when no logo is set. Precedence: per-org
 *  logo → deployment logo (same polarity-preference) → null. */
export function logoUrlFor(
  branding: OrgBranding | null,
  resolved: ResolvedPolarity,
  deployment: DeploymentBranding | null = null,
): string | null {
  const pick = (logo: OrgBranding['logo']): string | null => {
    if (!logo) return null;
    const primary = resolved === 'dark' ? logo.darkUrl : logo.lightUrl;
    const fallback = resolved === 'dark' ? logo.lightUrl : logo.darkUrl;
    return primary ?? fallback ?? null;
  };
  return pick(branding?.logo) ?? pick(deployment?.logo);
}

function setVar(el: HTMLElement, name: string, value: string | null): void {
  if (value) el.style.setProperty(name, value);
  else el.style.removeProperty(name);
}

function setFavicon(href: string): void {
  let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.href = href;
}

/** Apply branding to <html> / <head> / document.title. `branding === null`
 *  falls back to the `deployment` tier, then to the neutral defaults (token
 *  accent, neutral title + favicon). Precedence per field: per-org →
 *  deployment → hardwired neutral. */
export function applyBranding(
  branding: OrgBranding | null,
  resolved: ResolvedPolarity,
  deployment: DeploymentBranding | null = null,
): void {
  const el = document.documentElement;
  const { accent, accentSecondary } = accentFor(branding, resolved, deployment);
  setVar(el, '--color-accent', accent);
  setVar(el, '--color-accent-secondary', accentSecondary);
  // D23 + D23-ext: derive the AA-safe on-accent foreground for BOTH the primary and the secondary
  // accent (this polarity) and set them INLINE (same cascade rule as the accent — D7 addendum).
  // Re-runs on polarity flip with the new accents. A null accent (token default) → remove so the
  // `.on-accent*` CSS default (rgb(0 0 0)) applies. Same `onAccentFor` util for both — only the input
  // accent differs, so the secondary stays AA for an arbitrary tenant `accentSecondary` too.
  setVar(el, '--color-on-accent', onAccentFor(accent));
  setVar(el, '--color-on-accent-secondary', onAccentFor(accentSecondary));
  document.title =
    branding?.productName ?? deployment?.productName ?? NEUTRAL_PRODUCT_NAME;
  setFavicon(branding?.faviconUrl ?? deployment?.faviconUrl ?? NEUTRAL_FAVICON_HREF);
}
